import { AlarmClock, Eye, Pencil } from 'lucide-react';
import { Link } from 'react-router';
import { cn } from '../../../lib/utils';
import { PortalCard, SkeletonGroup, StatusPill } from '../../portal';
import { OverviewSummaryColumnSkeleton } from './OverviewSkeleton';
import type { SummaryColumn } from './overviewSummary';

const PILL_ICONS = {
  eye: <Eye />,
  pencil: <Pencil />,
  clock: <AlarmClock />,
};

function SummaryColumnView({ column }: { column: SummaryColumn }) {
  return (
    <>
      <span className="text-xs leading-4 text-seeker-400">{column.label}</span>
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            'min-w-0 truncate text-4xl font-medium leading-10 tabular-nums',
            column.valueMuted ? 'text-seeker-400' : 'text-white',
          )}
          title={column.value}
        >
          {column.value}
        </span>
        <StatusPill
          tone={column.pillTone}
          icon={column.pillIcon ? PILL_ICONS[column.pillIcon] : undefined}
        >
          {column.pillLabel}
        </StatusPill>
      </div>
      <span className="text-xs leading-4 text-seeker-400">
        {column.caption}
      </span>
    </>
  );
}

interface OverviewSummaryCardProps {
  columns: [SummaryColumn, SummaryColumn, SummaryColumn];
  storeListingPath: string | null;
  isLoading: boolean;
}

export function OverviewSummaryCard({
  columns,
  storeListingPath,
  isLoading,
}: OverviewSummaryCardProps) {
  if (isLoading) {
    return (
      <SkeletonGroup label="Loading release summary">
        <PortalCard className="grid gap-8 px-6 py-8 md:grid-cols-3 lg:px-[74px] lg:py-10">
          {columns.map((column) => (
            <OverviewSummaryColumnSkeleton key={column.label} />
          ))}
        </PortalCard>
      </SkeletonGroup>
    );
  }

  // Keyed on the shown values so a column fades in when its value changes
  // (e.g. store status landing later) but stays put on identical refetches.
  const columnClass = 'motion-enter flex min-w-0 flex-col gap-2';

  return (
    <PortalCard className="grid gap-8 px-6 py-8 md:grid-cols-3 lg:px-[74px] lg:py-10">
      {columns.map((column, index) => {
        const key = `${column.label}:${column.value}:${column.pillLabel}`;
        return index === 2 && storeListingPath ? (
          <Link
            key={key}
            to={storeListingPath}
            className={cn(
              columnClass,
              // Background hover (not opacity): `motion-enter` pins opacity once it ends.
              '-m-2 rounded-lg p-2 transition-colors duration-150 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
            )}
          >
            <SummaryColumnView column={column} />
          </Link>
        ) : (
          <div key={key} className={columnClass}>
            <SummaryColumnView column={column} />
          </div>
        );
      })}
    </PortalCard>
  );
}
